const MAX_FEEDBACK_PHOTO_BYTES = 1536 * 1024;
const TARGET_FEEDBACK_PHOTO_BYTES = 1450 * 1024;

function isHeic(file: File) {
  return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

async function browserImageSource(blob: Blob) {
  try {
    const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
    return {
      source: bitmap as CanvasImageSource,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close(),
    };
  } catch {
    const url = URL.createObjectURL(blob);
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Fotoğraf açılamadı."));
      element.src = url;
    });
    return {
      source: image as CanvasImageSource,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  }
}

function canvasBlob(canvas: HTMLCanvasElement, type: "image/webp" | "image/jpeg", quality: number) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
}

async function smallestEncoding(canvas: HTMLCanvasElement, quality: number) {
  const candidates = await Promise.all([
    canvasBlob(canvas, "image/webp", quality),
    canvasBlob(canvas, "image/jpeg", quality),
  ]);
  return candidates
    .filter((candidate): candidate is Blob => Boolean(candidate) && ["image/webp", "image/jpeg"].includes(candidate!.type))
    .sort((left, right) => left.size - right.size)[0] ?? null;
}

export async function optimizedFeedbackPhoto(file: File, vehiclePhoto = false) {
  let input: Blob = file;
  if (isHeic(file)) {
    try {
      const { heicTo } = await import("heic-to/csp");
      const converted = await heicTo({ blob: file, type: "image/jpeg", quality: 0.86 });
      input = converted instanceof Blob ? converted : new Blob([converted], { type: "image/jpeg" });
    } catch {
      throw new Error(`${file.name} adlı iPhone fotoğrafı dönüştürülemedi.`);
    }
  }

  const decoded = await browserImageSource(input);
  try {
    if (!decoded.width || !decoded.height) throw new Error(`${file.name} geçerli bir fotoğraf değil.`);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("Fotoğraf işlenemedi.");

    const longestEdge = Math.max(decoded.width, decoded.height);
    const initialScale = Math.min(1, 1800 / longestEdge);
    const qualitySteps = vehiclePhoto ? [0.86, 0.8, 0.74, 0.68, 0.62] : [0.86, 0.74, 0.62, 0.5, 0.4];
    const maxBytes = vehiclePhoto ? 1000000 : MAX_FEEDBACK_PHOTO_BYTES;
    const targetBytes = vehiclePhoto ? 950000 : TARGET_FEEDBACK_PHOTO_BYTES;
    // Preserve at least a 720px short edge when the source and 1800px cap allow it.
    // Never upscale a small original or change its aspect ratio.
    const minimumScale = vehiclePhoto ? Math.min(initialScale, 720 / Math.min(decoded.width, decoded.height)) : 0;
    let dimensionScale = initialScale;
    let smallest: Blob | null = null;

    // Kalite azaltımı tek başına özellikle ayrıntılı ekran görüntülerinde yeterli
    // olmayabilir. Her turda çözünürlüğü de düşürerek sunucu sınırını garanti ederiz.
    for (let resizeAttempt = 0; resizeAttempt < 7; resizeAttempt += 1) {
      canvas.width = Math.max(1, Math.round(decoded.width * dimensionScale));
      canvas.height = Math.max(1, Math.round(decoded.height * dimensionScale));
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);

      for (const quality of qualitySteps) {
        const output = await smallestEncoding(canvas, quality);
        if (!output) continue;
        if (!smallest || output.size < smallest.size) smallest = output;
        if (output.size <= targetBytes) {
          const extension = output.type === "image/webp" ? "webp" : "jpg";
          const baseName = file.name.replace(/\.[^.]+$/, "") || "geri-bildirim";
          return new File([output], `${baseName}.${extension}`, { type: output.type });
        }
      }
      if (dimensionScale === minimumScale) break;
      dimensionScale = Math.max(minimumScale, dimensionScale * 0.78);
    }

    if (smallest && smallest.size <= maxBytes) {
      const extension = smallest.type === "image/webp" ? "webp" : "jpg";
      return new File([smallest], `${file.name.replace(/\.[^.]+$/, "") || "geri-bildirim"}.${extension}`, { type: smallest.type });
    }
    throw new Error(`${file.name} güvenli boyuta küçültülemedi. Lütfen başka bir fotoğraf deneyin.`);
  } finally {
    decoded.release();
  }
}
